import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TaskCard } from "@/components/TaskCard";
import { Compass, Brain, HandHeart, Check, Sparkles, Trophy, ChevronRight } from "lucide-react";

export default function Today() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getToday());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (task) => {
    await api.updateTask(task.id, { status: "done" });
    toast.success("Nice. One thing done.");
    load();
  };
  const skip = async (task) => {
    await api.updateTask(task.id, { status: "skipped" });
    toast("Skipped — no worries.");
    load();
  };

  const completeDeposit = async () => {
    await api.completeTrustDeposit(data.trust_deposit.id);
    toast.success("Trust deposited.");
    load();
  };

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });

  return (
    <div className="px-4 pt-12 pb-28 min-h-screen nrt-fade">
      <header className="mb-7">
        <p className="text-sm text-[#7FB0B5] font-medium">{dateLabel}</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-zinc-50 mt-1">
          Today
        </h1>
      </header>

      {/* Two big actions */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <button
          data-testid="today-next-button"
          onClick={() => navigate("/next")}
          className="bg-[#365458] hover:bg-[#2A4346] rounded-2xl p-5 flex flex-col items-start gap-3 min-h-[120px] transition-colors active:scale-[0.98]"
        >
          <Compass className="w-7 h-7 text-white" />
          <span className="text-white font-bold text-base text-left leading-tight">What should I do next?</span>
        </button>
        <button
          data-testid="today-braindump-button"
          onClick={() => navigate("/brain-dump")}
          className="bg-zinc-800 hover:bg-zinc-700 rounded-2xl p-5 flex flex-col items-start gap-3 min-h-[120px] transition-colors active:scale-[0.98]"
        >
          <Brain className="w-7 h-7 text-[#7FB0B5]" />
          <span className="text-zinc-100 font-bold text-base text-left leading-tight">Brain dump</span>
        </button>
      </div>

      {/* Must do today */}
      <section className="mb-8">
        <h2 className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3">
          Must do today
        </h2>
        {loading ? (
          <p className="text-zinc-600 text-sm">Loading…</p>
        ) : data?.must_do_today?.length ? (
          <div className="space-y-3" data-testid="must-do-list">
            {data.must_do_today.map((t) => (
              <TaskCard key={t.id} task={t} onComplete={complete} onSkip={skip} />
            ))}
          </div>
        ) : (
          <div className="bg-zinc-800/60 rounded-2xl p-6 text-center">
            <p className="text-zinc-400 text-sm">Nothing locked in yet. Pick up to 3 things that matter today.</p>
          </div>
        )}
      </section>

      {/* Trust deposit */}
      <section className="mb-8">
        <h2 className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3">
          Today's trust deposit
        </h2>
        {data?.trust_deposit ? (
          <div className="bg-zinc-100 rounded-2xl p-5 flex items-center gap-4" data-testid="today-trust-deposit">
            <HandHeart className="w-6 h-6 text-[#365458] shrink-0" />
            <div className="flex-1">
              <p className={`font-semibold text-zinc-900 ${data.trust_deposit.completed ? "line-through opacity-60" : ""}`}>
                {data.trust_deposit.promise}
              </p>
            </div>
            {data.trust_deposit.completed ? (
              <span className="text-[#365458] text-sm font-bold inline-flex items-center gap-1">
                <Check className="w-4 h-4" /> Done
              </span>
            ) : (
              <button
                data-testid="today-complete-deposit"
                onClick={completeDeposit}
                className="bg-[#365458] text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-[#2A4346]"
              >
                Keep it
              </button>
            )}
          </div>
        ) : (
          <button
            data-testid="today-set-deposit"
            onClick={() => navigate("/trust")}
            className="w-full bg-zinc-800/60 rounded-2xl p-5 text-left flex items-center gap-3 hover:bg-zinc-800"
          >
            <Sparkles className="w-5 h-5 text-[#7FB0B5]" />
            <span className="text-zinc-300 text-sm">Choose one small promise to keep today</span>
          </button>
        )}
      </section>

      {/* Wins today — quiet, zero-guilt */}
      {data?.wins && (() => {
        const total = data.wins.tasks_done + data.wins.deposits_done;
        return (
          <button
            data-testid="wins-today-card"
            onClick={() => navigate("/wins")}
            className="w-full text-left bg-zinc-100 rounded-2xl p-5 mb-8 shadow-md transition-colors hover:bg-white active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#365458]/12 flex items-center justify-center shrink-0">
                <Trophy className="w-5 h-5 text-[#365458]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-zinc-900">
                  {total > 0 ? `Wins today: ${total}` : "No wins logged yet"}
                </p>
                <p className="text-sm text-zinc-500">
                  {total > 0 ? "Proof you moved today." : "Anything you finish shows up here."}
                </p>
                {total > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="wins-today-items">
                    {data.wins.tasks.slice(0, 3).map((t) => (
                      <span key={t.id} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600 truncate max-w-[150px]">
                        {t.title}
                      </span>
                    ))}
                    {data.wins.deposits.slice(0, 1).map((d) => (
                      <span key={d.id} className="text-[11px] px-2 py-0.5 rounded-full bg-[#365458]/12 text-[#2A4346] truncate max-w-[150px]">
                        {d.promise}
                      </span>
                    ))}
                    {total > 4 && <span className="text-[11px] px-2 py-0.5 text-zinc-500">+{total - 4} more</span>}
                  </div>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-zinc-400 shrink-0" />
            </div>
          </button>
        );
      })()}

      {/* Bonus tasks */}
      {data?.bonus_tasks?.length > 0 && (
        <section>
          <h2 className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3">
            Bonus, if you have it in you
          </h2>
          <div className="space-y-3" data-testid="bonus-list">
            {data.bonus_tasks.map((t) => (
              <TaskCard key={t.id} task={t} onComplete={complete} onSkip={skip} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
